// Shared DOM building + positioning for the styled Plantafel/Planning hover tooltip.
//
// Two owners render through this module: `plantafel_tooltip_controller.js` (one Stimulus
// instance per node — Plantafel proper, which also needs the legacy inline-tooltip mode) and
// `planning_tooltip_delegate_controller.js` (one delegated instance per capacity-planning
// surface). Keeping the tooltip markup and placement here stops the two from drifting apart.

const ICON_COLORS = {
  warning: "text-red-400",
  info: "text-orange-400",
  task: "text-blue-400",
  calendar: "text-blue-400",
  check: "text-green-400",
  trade: "text-purple-400",
  dispatched: "text-green-400",
  assigned: "text-emerald-300",
  label: "text-gray-300",
  conflict: "text-orange-400",
  pending: "text-gray-400",
  rejected: "text-red-400",
}

const ICON_PATHS = {
  warning: [
    {
      d: "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z",
      fillRule: "evenodd",
      clipRule: "evenodd",
    },
  ],
  info: [
    {
      d: "M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z",
      fillRule: "evenodd",
      clipRule: "evenodd",
    },
  ],
  task: [
    { d: "M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" },
    {
      d: "M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z",
      fillRule: "evenodd",
      clipRule: "evenodd",
    },
  ],
  calendar: [
    {
      d: "M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z",
      fillRule: "evenodd",
      clipRule: "evenodd",
    },
  ],
  check: [
    {
      d: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z",
      fillRule: "evenodd",
      clipRule: "evenodd",
    },
  ],
  dispatched: [
    { d: "M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" },
    {
      d: "M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z",
      fillRule: "evenodd",
      clipRule: "evenodd",
    },
  ],
  // Heroicons (mini) user — "bereits zugewiesen" on the capacity board.
  assigned: [
    { d: "M10 8a3 3 0 100-6 3 3 0 000 6z" },
    {
      d: "M3.465 14.493a1.23 1.23 0 00.41 1.412A9.957 9.957 0 0010 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 00-13.074.003z",
    },
  ],
  // Heroicons (mini) tag — a capacity allocation's free-text label.
  label: [
    {
      d: "M5.5 3A2.5 2.5 0 003 5.5v2.879a2.5 2.5 0 00.732 1.767l6.5 6.5a2.5 2.5 0 003.536 0l2.878-2.878a2.5 2.5 0 000-3.536l-6.5-6.5A2.5 2.5 0 008.38 3H5.5zM6 7a1 1 0 100-2 1 1 0 000 2z",
      fillRule: "evenodd",
      clipRule: "evenodd",
    },
  ],
  // Heroicons (mini) clock — an entry still waiting for approval.
  pending: [
    {
      d: "M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z",
      fillRule: "evenodd",
      clipRule: "evenodd",
    },
  ],
  // Heroicons (mini) x-circle — a rejected entry.
  rejected: [
    {
      d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z",
      fillRule: "evenodd",
      clipRule: "evenodd",
    },
  ],
  trade: [
    {
      d: "M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z",
      fillRule: "evenodd",
      clipRule: "evenodd",
    },
    {
      d: "M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z",
    },
  ],
}

// Same triangle as `warning`, only coloured in the orange of the Stundenzettel list's "Konflikt" badge.
ICON_PATHS.conflict = ICON_PATHS.warning

function buildIcon(iconType) {
  const paths = ICON_PATHS[iconType]
  if (!paths) return null

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  svg.setAttribute("class", `w-4 h-4 shrink-0 mt-0.5 ${ICON_COLORS[iconType] || "text-gray-400"}`)
  svg.setAttribute("fill", "currentColor")
  svg.setAttribute("viewBox", "0 0 20 20")

  paths.forEach((p) => {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    if (p.d) path.setAttribute("d", p.d)
    if (p.fillRule) path.setAttribute("fill-rule", p.fillRule)
    if (p.clipRule) path.setAttribute("clip-rule", p.clipRule)
    svg.appendChild(path)
  })

  return svg
}

// A list entry: plain text, or a link opening in a new tab. Only app-relative hrefs are linked —
// the payload is server-rendered, but a `javascript:` URL must never become clickable.
function buildListEntry(entry) {
  if (entry && typeof entry === "object") {
    const href = typeof entry.href === "string" && entry.href.startsWith("/") ? entry.href : null
    if (!href) return document.createTextNode(entry.text ?? "")

    const link = document.createElement("a")
    link.href = href
    link.target = "_blank"
    link.rel = "noopener"
    link.className =
      "text-white underline decoration-gray-500 underline-offset-2 hover:decoration-white"
    link.textContent = entry.text ?? ""
    return link
  }
  return document.createTextNode(entry ?? "")
}

// Whether a built tooltip holds something to click — the delegate then keeps it open while the
// pointer travels from the hovered node into it.
export function interactiveTooltip(tooltip) {
  return !!tooltip?.querySelector("a[href]")
}

// Builds the dynamic-mode tooltip for an element carrying the JSON payload in
// `data-plantafel-tooltip-data-value`, appends it (hidden) to <body>, and returns it.
// Returns null when the payload is missing or unparseable.
export function buildTooltipFor(element) {
  const dataAttr = element.dataset.plantafelTooltipDataValue
  if (!dataAttr) return null

  let data
  try {
    data = JSON.parse(dataAttr)
  } catch (_e) {
    return null
  }

  const tooltip = document.createElement("div")
  tooltip.className = "plantafel-dynamic-tooltip hidden"

  const inner = document.createElement("div")
  inner.className =
    "bg-gray-900 text-white text-sm rounded-lg px-4 py-3 shadow-lg max-w-xs whitespace-normal"

  const nameDiv = document.createElement("div")
  nameDiv.className = "font-medium mb-2"
  nameDiv.textContent = data.name
  inner.appendChild(nameDiv)

  if (data.items) {
    data.items.forEach((item, index) => {
      const row = document.createElement("div")
      row.className = `flex items-start gap-2${index > 0 ? " mt-2" : ""}`

      const icon = buildIcon(item.icon)
      if (icon) row.appendChild(icon)

      const content = document.createElement("div")
      content.className = "min-w-0"

      const text = document.createElement("p")
      text.className = "text-gray-100"
      text.textContent = item.text
      content.appendChild(text)

      // Optional bulleted list under the text (e.g. the elements of a double-booking). An entry
      // may be a link ({ text, href }), e.g. the task a Beauftragung created.
      if (Array.isArray(item.list) && item.list.length > 0) {
        const list = document.createElement("ul")
        list.className = "mt-1 list-disc space-y-0.5 pl-4 text-gray-300"
        item.list.forEach((entry) => {
          const li = document.createElement("li")
          li.appendChild(buildListEntry(entry))
          list.appendChild(li)
        })
        content.appendChild(list)
      }

      row.appendChild(content)
      inner.appendChild(row)
    })
  }

  const arrow = document.createElement("div")
  arrow.className =
    "absolute left-1/2 -translate-x-1/2 -top-2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-gray-900"
  inner.appendChild(arrow)

  tooltip.appendChild(inner)
  document.body.appendChild(tooltip)
  return tooltip
}

// Hide ALL visible tooltips on the page (dynamic ones and the legacy inline ones that were
// moved to <body>) — showing a new tooltip must never leave a stale one behind.
export function hideAllTooltips() {
  document.querySelectorAll(".plantafel-dynamic-tooltip").forEach((tooltip) => {
    hideTooltip(tooltip)
  })
  document.querySelectorAll('body > [id$="-tooltip"]').forEach((tooltip) => {
    hideTooltip(tooltip)
  })
  document
    .querySelectorAll('[data-plantafel-tooltip-target="tooltip"]:not(.hidden)')
    .forEach((tooltip) => {
      hideTooltip(tooltip)
    })
}

export function hideTooltip(tooltip) {
  tooltip.classList.add("hidden")
  tooltip.style.visibility = "hidden"
  tooltip.style.display = "none"
}

const GAP = 8
const VIEWPORT_MARGIN = 10

const ARROW_CLASSES = {
  below:
    "absolute left-1/2 -translate-x-1/2 -top-2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-gray-900",
  above:
    "absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-gray-900",
  right:
    "absolute -left-2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-gray-900",
  left: "absolute -right-2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-gray-900",
}

const ARROW_SELECTOR = [
  '[class*="border-b-gray-900"]',
  '[class*="border-t-gray-900"]',
  '[class*="border-r-gray-900"]',
  '[class*="border-l-gray-900"]',
].join(", ")

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max))

// Below, else above, else beside the anchor (a tall anchor — a timeline card listing many
// Zulagen — leaves room on neither vertical side), else whichever vertical side is roomier with
// the height capped so the tooltip scrolls instead of leaving the viewport.
function choosePlacement(rect, width, height) {
  const space = {
    below: window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN,
    above: rect.top - GAP - VIEWPORT_MARGIN,
    right: window.innerWidth - rect.right - GAP - VIEWPORT_MARGIN,
    left: rect.left - GAP - VIEWPORT_MARGIN,
  }
  const viewportHeight = window.innerHeight - 2 * VIEWPORT_MARGIN

  if (space.below >= height) return { side: "below" }
  if (space.above >= height) return { side: "above" }
  if (space.right >= width) return { side: "right", maxHeight: viewportHeight }
  if (space.left >= width) return { side: "left", maxHeight: viewportHeight }
  return space.above > space.below
    ? { side: "above", maxHeight: space.above }
    : { side: "below", maxHeight: space.below }
}

function coordinatesFor(side, rect, width, height) {
  if (side === "below" || side === "above") {
    const left = clamp(
      rect.left + rect.width / 2 - width / 2,
      VIEWPORT_MARGIN,
      window.innerWidth - width - VIEWPORT_MARGIN,
    )
    return { left, top: side === "below" ? rect.bottom + GAP : rect.top - height - GAP }
  }

  const top = clamp(
    rect.top + rect.height / 2 - height / 2,
    VIEWPORT_MARGIN,
    window.innerHeight - height - VIEWPORT_MARGIN,
  )
  return { left: side === "right" ? rect.right + GAP : rect.left - width - GAP, top }
}

// Positions the tooltip fixed next to `anchorElement` so it stays inside the viewport (see
// choosePlacement), points the arrow at the anchor, and makes it visible.
export function positionTooltip(tooltip, anchorElement) {
  const rect = anchorElement.getBoundingClientRect()
  const arrow = tooltip.querySelector(ARROW_SELECTOR)
  // The arrow is positioned against the outer tooltip, so capping and scrolling the inner box
  // keeps the arrow unclipped.
  const scrollBox = tooltip.firstElementChild || tooltip

  // Show and position (invisible until correctly placed)
  tooltip.style.visibility = "hidden"
  tooltip.style.display = "block"
  tooltip.classList.remove("hidden")
  tooltip.style.position = "fixed"
  tooltip.style.transform = "none"
  tooltip.style.zIndex = "2147483647"
  scrollBox.style.maxHeight = ""
  scrollBox.style.overflowY = ""

  // Reset position to measure dimensions accurately
  tooltip.style.left = "0px"
  tooltip.style.top = "0px"

  const placement = choosePlacement(rect, tooltip.offsetWidth, tooltip.offsetHeight)
  if (placement.maxHeight && tooltip.offsetHeight > placement.maxHeight) {
    scrollBox.style.maxHeight = `${Math.max(placement.maxHeight, 0)}px`
    scrollBox.style.overflowY = "auto"
  }

  const width = tooltip.offsetWidth
  const height = tooltip.offsetHeight
  const { left, top } = coordinatesFor(placement.side, rect, width, height)

  if (arrow) {
    arrow.className = ARROW_CLASSES[placement.side]
    arrow.style.top = ""
    arrow.style.left = ""
    if (placement.side === "right" || placement.side === "left") {
      arrow.style.top = `${clamp(rect.top + rect.height / 2 - top, 12, height - 12)}px`
    } else {
      arrow.style.left = `${clamp(rect.left + rect.width / 2 - left, 12, width - 12)}px`
    }
  }

  // Apply initial position
  tooltip.style.left = `${left}px`
  tooltip.style.top = `${top}px`

  // Self-correct for containing block offsets (CSS transforms, overflow contexts, or other
  // properties can shift fixed positioning away from viewport coordinates). Compare where the
  // tooltip actually rendered vs where we intended, and compensate.
  const placed = tooltip.getBoundingClientRect()
  const offsetX = placed.left - left
  const offsetY = placed.top - top

  if (Math.abs(offsetX) > 1 || Math.abs(offsetY) > 1) {
    tooltip.style.left = `${left - offsetX}px`
    tooltip.style.top = `${top - offsetY}px`
  }

  tooltip.style.visibility = "visible"
}
